/**
 * Centralised query-key factory.
 *
 * Naming convention:
 *   queryKeys.<domain>.all        → broadest cache scope (for invalidation)
 *   queryKeys.<domain>.list(…)    → list with optional filters
 *   queryKeys.<domain>.detail(id) → single entity
 *
 * Usage:
 *   useQuery({ queryKey: queryKeys.fleet.list(), queryFn: … })
 *   queryClient.invalidateQueries({ queryKey: queryKeys.fleet.all })
 */

const queryKeys = {
  // ── Fleet / Charger list ──
  fleet: {
    all: ["fleet"],
    list: (filters = {}) => ["fleet", "list", filters],
    detail: serialNumber => ["fleet", "detail", serialNumber],
    stats: () => ["fleet", "stats"],
  },

  // ── Fleet Hybrid Search (OpenSearch + MongoDB) ──
  fleetHybrid: {
    all: ["fleetHybrid"],
    search: (params = {}) => ["fleetHybrid", "search", params],
  },

  // ── Site / System Info ──
  sites: {
    all: ["sites"],
    detail: siteId => ["sites", "detail", siteId],
    systemInfo: siteId => ["sites", "systemInfo", siteId],
    locations: fleetDataHash => ["sites", "locations", fleetDataHash],
  },

  // ── Utilization ──
  utilization: {
    all: ["utilization"],
    bySite: (siteId, startDate, endDate) => ["utilization", "site", siteId, startDate, endDate],
    byStation: (stationId, startDate, endDate) => ["utilization", "station", stationId, startDate, endDate],
    fleet: (startDate, endDate) => ["utilization", "fleet", startDate, endDate],
  },

  // ── Charger Details ──
  charger: {
    all: ["charger"],
    config: (siteId, chargerId) => ["charger", "config", siteId, chargerId],
    status: (serialNo, startTime, endTime) => ["charger", "status", serialNo, startTime, endTime],
    matrix: (serialNo, startTime, endTime, type) => ["charger", "matrix", serialNo, startTime, endTime, type],
    powerOfferings: (serialNo, startTime, endTime) => ["charger", "powerOfferings", serialNo, startTime, endTime],
    hierarchy: () => ["charger", "hierarchy"],
  },

  // ── Events ──
  events: {
    all: ["events"],
    byCharger: (serialNumber, params) => ["events", serialNumber, params],
  },

  // ── OCPP Messages ──
  ocpp: {
    all: ["ocpp"],
    messages: (serialNumber, params) => ["ocpp", "messages", serialNumber, params],
  },

  // ── Users ──
  users: {
    all: ["users"],
    list: () => ["users", "list"],
    detail: email => ["users", "detail", email],
    permissions: email => ["users", "permissions", email],
  },

  // ── Feature Gating ──
  features: {
    all: ["features"],
    list: () => ["features", "list"],
    detail: featureId => ["features", "detail", featureId],
  },

  // ── Logs ──
  logs: {
    all: ["logs"],
    list: params => ["logs", "list", params],
    latest: params => ["logs", "latest", params],
  },
};

export default queryKeys;
